import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate ,} from 'react-router-dom';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const NewProgressParameter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const progressAttributeId = urlParams.get("progressAttributeId");
  const sportId = urlParams.get("sportId");
  const parameterId = urlParams.get('academyId');

  const token = localStorage.getItem('auth-token');
  const [name, setName] = useState('');
  const baselineRef = useRef(null);

  const buildSubmitPayload = (value = name) => ({
    id: parameterId || null,
    sportId: sportId ? Number(sportId) : null,
    progressAttributeId: progressAttributeId ? Number(progressAttributeId) : null,
    name: (value || '').trim(),
  });

  useEffect(() => {
    if (!parameterId) {
      baselineRef.current = buildSubmitPayload();
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ADMIN_EDIT_PROGRESS_PARAMETER}?progressParameterId=${parameterId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const responseData = await response.json();
          const fetchedName = responseData.data?.sportProgressAttributeEditForm?.name || '';
          setName(fetchedName);
          baselineRef.current = buildSubmitPayload(fetchedName);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to fetch parameter details',
            toast: true,
            position: 'top-end',
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } catch {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Unable to fetch parameter details',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    };

    fetchData();
  }, [parameterId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildSubmitPayload();
  
    if (parameterId) {
      const { digest, html } = buildDiffPreview(baselineRef.current || {}, payload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          timer: 2500,
          showConfirmButton: false,
        });
        return;
      }
  
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }
  
    const apiUrl = `${process.env.REACT_APP_API_ADMIN_NEW_PROGRESS_PARAMETER}/${sportId}/${progressAttributeId}`;
  
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
  
      fireAudit({
        entity: 'progress_parameter',
        affected_id: parameterId || null,
        action: parameterId ? 'update' : 'create',
        section: 'SPORT',
        subSection: 'Progress Parameter',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: baselineRef.current || {},
        newPayload: payload,
        extra: { page: 'NewProgressParameter' },
      });
  
      if (response.ok && data.success !== false) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: parameterId
            ? 'Progress parameter updated successfully!'
            : 'Progress parameter created successfully!',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
        baselineRef.current = payload;
        navigate(-1);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to create/update progress parameter',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred. Please try again.',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };
  

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/sportList" className="breadcrumb-link">
          Sport List
        </Link>{' '}
        &gt; {parameterId ? 'Update Progress Parameter' : 'Create Progress Parameter'}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            id="parameter-name"
            type="text"
            name="parameterName"
            className={`walkin-form ${name ? 'has-value' : ''}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Name"
            required
          />
          {name && <label htmlFor="parameter-name">Name</label>}
        </div>

        <button className="submit-button" type="submit">
          {parameterId ? 'Update' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default NewProgressParameter;
